import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import { playerService, userService } from "../../services/api";
import toast from "react-hot-toast";

export const usePlayerProfile = () => {
  const { playerId } = useParams();
  const { user, currentRole } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  const [representationRequest, setRepresentationRequest] = useState(
    location.state?.representationRequest || null
  );
  const [activeTab, setActiveTab] = useState("statistics");
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  const [attributes, setAttributes] = useState(null);
  const [uniqueTournaments, setUniqueTournaments] = useState(null);

  // Check if current user is viewing their own profile
  const isOwnProfile =
    currentRole === ROLES.Player && user?.id && user.id.toString() === playerId;

  const isAgentOwnThisPlayer = user?.id === player?.agent?.account_id;

  const playerWyscoutId = useMemo(
    () => player?.player?.id || player?.id || playerId || null,
    [player, playerId]
  );

  // Main useEffect for initial page load - only loads profile data
  useEffect(() => {
    const fetchPlayerData = async () => {
      if (!playerId) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch both profile APIs in parallel
        const [publicProfileResponse, profileResponse] = await Promise.all([
          playerService.getPublicPlayerProfile(playerId),
          userService.getProfile(), // Current user profile
        ]);

        if (publicProfileResponse.success) {
          setPlayer(publicProfileResponse.data);
        } else {
          setError(
            publicProfileResponse.error || "Failed to load player data."
          );
          setLoading(false);
          return;
        }

        if (profileResponse.success) {
          setCurrentUserProfile(profileResponse.data);
        }

        // Only set loading to false after both APIs succeed
        setLoading(false);
      } catch (error) {
        console.error("Error loading player data:", error);
        setError("Failed to load player data.");
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [playerId]);

  // Reset state when player changes
  useEffect(() => {
    setAttributes(null);
    setUniqueTournaments(null);
  }, [playerId]);

  // URL-Umstellung (Marin 2026-08-24): the visible URL carries the platform
  // number. Old Wyscout-number links keep resolving (the server accepts both
  // id families) and are canonicalized here once the payload names the
  // platform id — after the replace the ids match, so this never loops.
  useEffect(() => {
    const canonicalId = player?.player?.platform_id;
    if (canonicalId && String(canonicalId) !== String(playerId)) {
      navigate(`/search/player/${canonicalId}`, { replace: true });
    }
  }, [player, playerId, navigate]);

  // Memoize the share URL
  const shareUrl = useMemo(
    () => `${window.location.origin}/search/player/${playerId}`,
    [playerId]
  );

  // Memoize representation request handlers
  const handleRejectRequest = useCallback(async () => {
    try {
      const response = await playerService.dismissRepresentationRequest(
        representationRequest.id
      );
      if (response.success) {
        toast.success("Request dismissed successfully");
        setRepresentationRequest(null);
        // Clear the navigation state
        navigate(`/search/player/${playerId}`, { replace: true });
      } else {
        toast.error(response.error || "Failed to dismiss request");
      }
    } catch (error) {
      console.error("Error dismissing request:", error);
      toast.error("Failed to dismiss request. Please try again.");
    }
  }, [representationRequest?.id, navigate, playerId]);

  const handleAcceptRequest = useCallback(() => {
    // The public profile API wraps the player like { player: {...}, team, market_value }.
    // InvitePlayer.js expects FLAT fields (wyscout_id, name, image_url, ...), so build
    // a flattened object instead of passing the wrapper through.
    const inner = player?.player || {};
    const selectedPlayer = {
      wyscout_id: inner.id || playerId,
      id: inner.id || playerId,
      name: inner.name,
      display_name: inner.name,
      first_name: inner.firstname,
      last_name: inner.lastname,
      image_url: inner.image_url,
      image_path: inner.image_url,
      // Email comes from the representation request when available — public profile
      // does not expose it.
      email: representationRequest?.player_email || representationRequest?.email || "",
    };
    navigate("/agent/invite-player", {
      state: {
        selectedPlayer,
        fromRepresentationRequest: true,
      },
    });
  }, [navigate, player, playerId, representationRequest]);

  // Memoize the share functionality
  const handleShare = useCallback(async (selectedUsers, shareUrl) => {
    // This function is no longer needed as sharing is handled directly in ShareActionSheet
    // Keeping it for backward compatibility but it won't be called
    console.log("Legacy share handler called - should not be used");
  }, []);

  // Memoize the tabs array
  const tabs = useMemo(
    () => [
      { id: "statistics", label: "Statistics" },
      { id: "attributes", label: "Attributes" },
      { id: "value", label: "Value" },
      { id: "matches", label: "Matches" },
      { id: "transfers", label: "Transfers" },
      { id: "highlights", label: "Highlights" },
    ],
    []
  );

  return {
    // Route params
    playerId,
    // Auth state
    user,
    currentRole,
    // Player data
    player,
    loading,
    error,
    currentUserProfile,
    // Profile checks
    isOwnProfile,
    isAgentOwnThisPlayer,
    playerWyscoutId,
    // Tab state
    activeTab,
    setActiveTab,
    tabs,
    // Representation request
    representationRequest,
    handleRejectRequest,
    handleAcceptRequest,
    // Share
    shareUrl,
    handleShare,
    // Attributes
    attributes,
    setAttributes,
    uniqueTournaments,
    setUniqueTournaments,
    // Navigation
    navigate,
  };
};
